import {
    closedNavDrawerWidthDesktop,
    isGlobalDrawerOpenAtom,
    openNavDrawerWidth,
} from '@/components/GlobalNavigationDrawer/lib/constants';
import { useReserveRootNavOffset } from '@/components/layout/rootNavOffset';
import { ReturnComponent } from '@/utils/reactTypes';
import MuiDrawer from '@mui/material/Drawer';
import { CSSObject, Theme, styled } from '@mui/material/styles';
import { useAtomValue } from 'jotai';

import { GlobalDrawerItems } from './GlobalDrawerItems';

// Width lives on the fixed, full-height paper — never on the docked root, which has
// height 0 (its only child, the paper, is position: fixed and out of flow) and so
// cannot reserve layout space anyway.
const paperMixin = (theme: Theme, open: boolean): CSSObject => ({
    width: open ? openNavDrawerWidth : closedNavDrawerWidthDesktop,
    overflowX: 'hidden',
    backgroundColor: '#F5F5F5', // Light grey background
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: open
            ? theme.transitions.duration.enteringScreen
            : theme.transitions.duration.leavingScreen,
    }),
});

const StyledDrawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    whiteSpace: 'nowrap',
    '& .MuiDrawer-paper': paperMixin(theme, !!open),
}));

export const GlobalDesktopNavigationDrawer = (): ReturnComponent => {
    const isDrawerOpen = useAtomValue(isGlobalDrawerOpenAtom);

    const width = isDrawerOpen ? openNavDrawerWidth : closedNavDrawerWidthDesktop;

    // Offset the app root (#root) instead of wrapping content in a flex/box. Keeps the
    // layout flat so the browser keeps native scroll restoration (body stays the scroll
    // container). The fixed drawer paper sits in this gutter.
    useReserveRootNavOffset({ paddingLeft: width });

    return (
        <StyledDrawer variant={'permanent'} open={isDrawerOpen}>
            <GlobalDrawerItems />
        </StyledDrawer>
    );
};
